import { Box, styled, SvgIconProps } from "@mui/material";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { cacheService, thumbnailService } from "../../../Core/Api/services";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import CachedIcon from "@mui/icons-material/Cached";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckIcon from "@mui/icons-material/Check";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import QuestionMarkOutlinedIcon from "@mui/icons-material/QuestionMarkOutlined";
import { AssetIconProps } from "../../Misc/AssetIcon/asset-icon";
import { Img } from "react-image";
import { useTranslation } from "react-i18next";
import { AssetTypeAlphaColor } from "../../../Utils/helpers";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import AssetThumbnail from "../../Misc/AssetThumbnail";

export type AssetCardBodyProps = {
  obj: DbAssetObjectInfos;
  selected?: boolean;
  isCombiningSource?: boolean;
  isUploading?: boolean;
  scale?: number;
  autoScroll?: boolean;
};

const AssetCardBody: FC<AssetCardBodyProps> = (props) => {
  const { t } = useTranslation();
  const {
    selected,
    obj,
    isCombiningSource,
    isUploading = false,
    scale = GlobalConst.DEFAULT_SCALE,
    autoScroll = false,
  } = props;
  const [mouseOffset, setMouseOffset] = useState(1);
  const [spliceThumbnailUrl, setSpliceThumbnailUrl] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [scrollTrigger, setScrollTrigger] = useState(-1);
  const [assetIconSize, setAssetIconSize] = useState<AssetIconProps["size"]>("small");
  const cardBodyRef = useRef<HTMLDivElement>(null);

  const [width, setWidth] = useState(0);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((event) => {
      if (event[0].contentBoxSize[0].inlineSize > 500) setAssetIconSize("large");
      else if (event[0].contentBoxSize[0].inlineSize > 350) setAssetIconSize("medium");
      else setAssetIconSize("small");

      setWidth(event[0].contentBoxSize[0].inlineSize);
    });

    if (cardBodyRef.current) resizeObserver.observe(cardBodyRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [cardBodyRef.current]);

  // Load thumbs
  useEffect(() => {
    if (obj.type === ObjectType.Video || obj.type === ObjectType.VideoClip)
      setSpliceThumbnailUrl(
        thumbnailService.GetMergeThumbsSpliceImageByObjectId(
          obj.id,
          GlobalConst.BROWSER_IMG_SPRITE_COUNT,
          cacheService.GetAuthKey()!
        )
      );
    setThumbnailUrl(thumbnailService.GetThumbRefByObjectId(obj.id, cacheService.GetAuthKey()!, ThumbnailSize.xmedium));
  }, [obj.id]);

  const backgroundColor = useMemo(() => {
    return `linear-gradient(225.52deg, ${AssetTypeAlphaColor(obj.type, 0.12)} 0%, ${AssetTypeAlphaColor(
      obj.type,
      0.02
    )} 80.71%, ${AssetTypeAlphaColor(obj.type, 0.08)} 100%)`;
  }, [obj.type]);

  const shouldShowStatus = useMemo(
    () =>
      ([ObjectStatus.Rejected, ObjectStatus.ParsedAndProbbed, ObjectStatus.RejectedAuto].includes(obj.status) &&
        obj.type !== ObjectType.Folder) ||
      isUploading ||
      obj.type === ObjectType.Unknown,
    [obj.status, obj.type, isUploading]
  );

  const shouldShowThumbnail = useMemo(() => {
    return !shouldShowStatus && ![ObjectType.Audio, ObjectType.Subtitle, ObjectType.AudioClip].includes(obj.type);
  }, [shouldShowStatus, obj.type]);

  const _renderIcons = useMemo(() => {
    return (
      <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 1 }}>
        {shouldShowStatus ? (
          <StatusIcon
            sx={{ position: "absolute", left: "8px", bottom: "8px" }}
            isUploading={isUploading}
            type={obj.type}
            status={obj.status}
            className="central-icon"
          />
        ) : (
          <AssetThumbnail type={obj.type} size="medium" translucent />
        )}
      </Box>
    );
  }, [thumbnailUrl, shouldShowStatus, obj.type]);

  const _renderThumbnail = useMemo(() => {
    // Folder with or without ref thumbnail
    if (obj.type === ObjectType.Folder && !shouldShowStatus)
      return (
        <CardThumbnail backgroundColor={backgroundColor}>
          <Img
            style={{
              position: "relative",
              overflow: "hidden",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: 2,
            }}
            src={thumbnailUrl}
          />
          {_renderIcons}
        </CardThumbnail>
      );

    // Video stripes
    if (obj.type === ObjectType.Video || obj.type === ObjectType.VideoClip)
      return (
        <CardThumbnail backgroundColor={backgroundColor}>
          <Box
            onMouseLeave={(e) => setMouseOffset(1)}
            onMouseMove={(e) => {
              if (!(spliceThumbnailUrl && spliceThumbnailUrl !== "")) return;
              if (selected || autoScroll) return;

              let posInDiv = e.clientX - e.currentTarget.getBoundingClientRect().left;

              if (width === 0) return;

              let blockWidth = width / GlobalConst.BROWSER_IMG_SPRITE_COUNT; // Size of each block in px
              let percent = posInDiv / width; // Position in the div in %
              let blockIndex = Math.trunc((percent * width) / blockWidth); // Position in the div in blocks

              let offset = Math.trunc(blockIndex * (width / 1.789));

              setMouseOffset(-offset);
            }}
            sx={{
              position: "absolute",
              zIndex: 2,
              overflow: "hidden",
              width: "100%",
              height: "100%",
              background:
                mouseOffset !== 1
                  ? "url('" + spliceThumbnailUrl + "') 0px " + mouseOffset + "px"
                  : "url('" + thumbnailUrl + "')",
              backgroundSize: mouseOffset !== 1 ? "cover" : "cover",
              backgroundPosition: mouseOffset !== 1 ? "auto" : "center",
              backgroundRepeat: "no-repeat",
            }}
          ></Box>
          {_renderIcons}
        </CardThumbnail>
      );

    return <CardThumbnail backgroundColor={backgroundColor}>{_renderIcons}</CardThumbnail>;
  }, [
    shouldShowStatus,
    selected,
    mouseOffset,
    spliceThumbnailUrl,
    thumbnailUrl,
    obj?.id,
    scrollTrigger,
    assetIconSize,
  ]);

  useEffect(() => {
    if (autoScroll) {
      setTimeout(
        () => {
          setScrollTrigger((x) => (x >= GlobalConst.BROWSER_IMG_SPRITE_COUNT ? 0 : x + 1));
        },
        scrollTrigger === -1 ? 720 : 360
      );
    } else {
      setScrollTrigger(-1);
      setMouseOffset(1);
    }
  }, [autoScroll, scrollTrigger]);
  useEffect(() => {
    if (scrollTrigger === -1) return;
    let offset = scrollTrigger * GlobalConst.BROWSER_IMG_SPRITE_HEIGHT_PX - scrollTrigger;
    setMouseOffset(-offset);
  }, [scrollTrigger]);

  return (
    <CardBody
      spliceThumbnailUrl={shouldShowThumbnail ? spliceThumbnailUrl : ""}
      thumbnailUrl={shouldShowThumbnail ? thumbnailUrl : ""}
      backgroundColor={backgroundColor}
      mouseOffset={mouseOffset}
      scale={scale}
      ref={cardBodyRef}
    >
      {_renderThumbnail}
    </CardBody>
  );
};

export default AssetCardBody;

const CardBody = styled("div", {
  shouldForwardProp: (propName) =>
    propName !== "mouseOffset" &&
    propName !== "thumbnailUrl" &&
    propName !== "spliceThumbnailUrl" &&
    propName !== "backgroundColor" &&
    propName !== "scale",
})<{
  mouseOffset: number; // css image sprite
  spliceThumbnailUrl: string;
  thumbnailUrl: string;
  backgroundColor: string;
  scale: number;
}>(({ theme, mouseOffset, thumbnailUrl, spliceThumbnailUrl, backgroundColor, scale }) => ({
  width: "100%",
  aspectRatio: "16/9",
  ".hoverchips": {
    position: "absolute",
    opacity: 0,
    bottom: "8px",
    right: "8px",
  },
  "&:hover": {
    ".hoverchips": {
      opacity: 1,
      transition: "all .4s",
    },
    "figure:before": {
      opacity: 1,
    },
  },
}));

export const CardThumbnail = styled(Box, { shouldForwardProp: (propName) => propName !== "backgroundColor" })<{
  backgroundColor: string;
}>(({ theme, backgroundColor }) => ({
  background: backgroundColor,
  borderRadius: "4px",
  overflow: "hidden",
  width: "100%",
  height: "auto",
  aspectRatio: "16/9",
  position: "relative",
}));

interface StatusIconProps extends SvgIconProps {
  isUploading?: boolean;
  status: ObjectStatus;
  type?: ObjectType;
}

const StatusIcon: React.FC<StatusIconProps> = ({
  isUploading = false,
  status,
  type = ObjectType.Unknown,
  ...otherProps
}) => {
  if (isUploading && type === ObjectType.Group) {
    return <FolderOutlinedIcon {...otherProps} />;
  }
  if (isUploading) {
    return <FileUploadOutlinedIcon color="primary" {...otherProps} />;
  }

  if (type === ObjectType.Unknown) {
    return <QuestionMarkOutlinedIcon color="primary" {...otherProps} />;
  }

  switch (status) {
    case ObjectStatus.ParsedAndProbbed:
      return <CachedIcon color="primary" {...otherProps} />;

    case ObjectStatus.ToBeQCed:
      return <VisibilityOutlinedIcon color="warning" {...otherProps} />;

    case ObjectStatus.New:
    case ObjectStatus.ToBeDigitalized:
      return <FileUploadOutlinedIcon color="info" {...otherProps} />;

    case ObjectStatus.Accepted:
      return <CheckIcon color="success" {...otherProps} />;

    case ObjectStatus.Rejected:
    case ObjectStatus.RejectedAuto:
      return <ErrorOutlineIcon color="error" {...otherProps} />;

    case ObjectStatus.Deleted:
      return <CancelOutlinedIcon color="inherit" {...otherProps} />;

    case ObjectStatus.WaitingForMEZZReference:
      return <HourglassEmptyIcon color="inherit" {...otherProps} />;

    case ObjectStatus.MissingMetadata:
      return <ReportProblemOutlinedIcon color="warning" {...otherProps} />;

    default:
      return <HourglassEmptyIcon color="inherit" {...otherProps} />;
  }
};
